/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/returns.scss';

import React, {KeyboardEventHandler, MouseEventHandler, useContext} from 'react';
import {
    AwesomeIcon,
    ChangeFunction,
    ElementPosition,
    Loader,
    MenuPlacement,
    OptionsMenu,
    OptionsMenuItem,
    SelectItemShape
} from '@dvrd/dvr-controls';
import BolReturn from "../../models/bolReturnModel";
import {BolmateInput, BolmateSelect} from "../controls";
import {formatSimpleDate, parseThousandSeparator} from "../../util/utils";
import classNames from 'classnames';
import {ProcessingResultTranslations, ReturnCounts, ReturnProcessingResult} from "../../util/interfaces";
import {Icon} from '@iconify/react';
import optionsHorizontal from '@iconify/icons-mi/options-horizontal';
import {getReturnOptions, ReturnOption, ReturnOptionType} from "../../util/returnUtil";
import {IconName} from '@fortawesome/fontawesome-svg-core';
import Image from "../misc/image";
import {AppContext} from "../misc/appContext";
import {userHasRight} from "../../util/accessUtil";
import InfoBanner from "../misc/infoBanner";

interface Props {
    onChangeSearch: ChangeFunction<string>;
    onEnterSearch: KeyboardEventHandler;
    onClickReturn: (bolReturn: BolReturn) => MouseEventHandler;
    onClickInvoice: (invoiceId: string) => MouseEventHandler;
    onChangePage: (page: number) => MouseEventHandler;
    onChangeStatus: ChangeFunction<ReturnProcessingResult | ''>;
    onClickSend: (invoiceId: string) => MouseEventHandler;
    onClickCredit: (bolReturn: BolReturn) => MouseEventHandler;
    onClickBol: MouseEventHandler;
    returns: BolReturn[];
    loading: boolean;
    search: string;
    page: number;
    limit: number;
    total: number;
    status: ReturnProcessingResult | '';
    counts: ReturnCounts;
    loadingCounts: boolean;
}

const statusItems: SelectItemShape[] = [
    {label: 'Alle', value: ''},
    {label: 'Openstaand', value: ReturnProcessingResult.OPEN},
    {label: 'Verwerkt', value: ReturnProcessingResult.ACCEPTED},
    {label: 'Geweigerd', value: ReturnProcessingResult.REJECTED},
    {label: 'Geannuleerd', value: ReturnProcessingResult.CANCELED},
]

export default function Returns(props: Props) {
    const context = useContext(AppContext);
    const {
        loading, search, onChangeSearch, onEnterSearch, returns, limit, page, onClickReturn, onClickInvoice, total,
        onChangePage, onChangeStatus, status, loadingCounts, counts, onClickSend, onClickCredit, onClickBol
    } = props;

    function onClickStatus(type: ReturnProcessingResult) {
        return function () {
            if (type === status) onChangeStatus('');
            else onChangeStatus(type);
        }
    }

    function _getReturnOptions(bolReturn: BolReturn): OptionsMenuItem[] {
        const options = getReturnOptions(bolReturn).filter((option: ReturnOption) => userHasRight(context, option.accessKey));
        return options.map((option: ReturnOption) => {
            let onClick: MouseEventHandler;
            if (option.type === ReturnOptionType.CREDIT) onClick = onClickCredit(bolReturn);
            else if (option.type === ReturnOptionType.INVOICE) onClick = onClickInvoice(bolReturn.invoiceId);
            else if (option.type === ReturnOptionType.CREDIT_INVOICE) onClick = onClickInvoice(bolReturn.creditInvoiceId);
            else if (option.type === ReturnOptionType.SEND) onClick = onClickSend(bolReturn.creditInvoiceId);
            else if (option.type === ReturnOptionType.BOL) onClick = onClickBol;
            else onClick = onClickReturn(bolReturn);
            return {onClick, icon: option.icon, label: option.label, disabled: option.disabled};
        })
    }

    function renderReturnsContainer() {
        return (
            <div className='returns-container'>
                <Loader active={loading}/>
                <div className='row head'>
                    <div/>
                    <label>RETOUR ID</label>
                    <label>DATUM</label>
                    <label>RMA ID</label>
                    <label>ORDER ID</label>
                    <label>STATUS</label>
                    <div/>
                </div>
                {renderReturns()}
            </div>
        )
    }

    function renderReturns() {
        if (!returns.length) {
            return renderNotFound();
        }
        return returns.map(renderReturn);
    }

    function renderReturn(bolReturn: BolReturn, index: number) {
        const imgElement = bolReturn.product?.imageUrl ?
                <img src={bolReturn.product.imageUrl} alt='' className='image'/> :
                <AwesomeIcon name='image' className='image-icon' prefix='far'/>,
            optionsRef = React.createRef<OptionsMenu>();

        function onClickRow() {
            optionsRef.current?.onOpenExternal();
        }

        return (
            <div key={index} className='row' onClick={onClickRow}>
                {imgElement}
                <label>{bolReturn.returnId}</label>
                <label>{formatSimpleDate(bolReturn.registeredAtMoment)}</label>
                <label>{bolReturn.rmaId}</label>
                <label>{bolReturn.orderId}</label>
                {renderReturnStatus(bolReturn)}
                <OptionsMenu options={_getReturnOptions(bolReturn)} placement={MenuPlacement.BOTTOM_LEFT}
                             optionsComponent={<Icon icon={optionsHorizontal} className='options-icon'/>}
                             containerClass='common-options' ref={optionsRef}/>
            </div>
        )
    }

    function renderReturnStatus(bolReturn: BolReturn) {
        const {isCredited, translatedStatus, statusColor, creditSent} = bolReturn;
        let columns = 1;
        if (isCredited) columns++;
        if (creditSent) columns++;
        return (
            <div className='return-status' style={{gridTemplateColumns: `repeat(${columns}, fit-content(100%)`}}>
                <label className={classNames('status', statusColor)}>{translatedStatus}</label>
                {isCredited && renderStatusCircle('purple', 'Creditfactuur aangemaakt', 'C', true)}
                {creditSent && renderStatusCircle('blue', 'Creditfactuur verzonden', 'paper-plane')}
            </div>
        )
    }

    function renderStatusCircle(color: string, label: string, icon: IconName | string, isTextIcon: boolean = false): React.ReactElement {
        const iconElement = isTextIcon ? <span className='status-label text'>{icon}</span> :
            <AwesomeIcon name={icon as IconName} className='status-label'/>;
        return (
            <div className={classNames('status-circle', color)}>
                {iconElement}
                <label className='status-full-label'>{label}</label>
            </div>
        )
    }

    function renderNotFound() {
        const terms: string[] = [];
        if (search.length) terms.push(`de zoekterm '${search}'`);
        if (status.length) terms.push(`de status ${ProcessingResultTranslations[status as ReturnProcessingResult]}`);
        const term = terms.length ? ` met ${terms.join(' en ')} ` : '';

        return (
            <div className='returns-image-container not-found'>
                <Image name='data-rows' className='returns-image'/>
                <label className='image-label'>Geen retouren gevonden</label>
                <label className='image-sub-label'>Er kunnen geen retouren{term}gevonden
                    worden.</label>
            </div>
        )
    }

    function renderPagerCount() {
        const start = Math.min(page * limit + 1, total), end = Math.min(total, (page + 1) * limit);
        return <label className='count-label'>{start}-{end} van {total}</label>
    }

    function renderPager(type: 'prev' | 'next') {
        let enabled: boolean;
        if (type === 'prev') enabled = page > 0;
        else {
            const maxPage = Math.ceil(total / limit);
            enabled = page < maxPage - 1;
        }

        let icon: IconName, changePage: number;
        if (type === 'prev') {
            icon = 'chevron-left';
            changePage = page - 1;
        } else {
            icon = 'chevron-right';
            changePage = page + 1;
        }
        return (
            <AwesomeIcon name={icon} className={classNames('pager-icon', enabled && 'enabled')}
                         onClick={enabled ? onChangePage(changePage) : undefined}/>
        );
    }

    function renderCounts() {
        return (
            <div className='counts-row'>
                {renderCount(ReturnProcessingResult.OPEN)}
                {renderCount(ReturnProcessingResult.ACCEPTED)}
                {renderCount(ReturnProcessingResult.REJECTED)}
                {renderCount(ReturnProcessingResult.CANCELED)}
            </div>
        )
    }

    function renderCount(type: ReturnProcessingResult) {
        let label: string, icon: IconName, color: string;
        const count: string = parseThousandSeparator(counts[type] ?? 0);
        const active = status === type;
        if (type === ReturnProcessingResult.OPEN) {
            label = 'openstaand';
            icon = 'truck-loading';
            color = 'orange';
        } else if (type === ReturnProcessingResult.ACCEPTED) {
            label = 'verwerkt';
            icon = 'check-circle';
            color = 'green';
        } else if (type === ReturnProcessingResult.REJECTED) {
            label = 'geweigerd';
            icon = 'times-circle';
            color = 'red';
        } else {
            label = 'geannuleerd';
            icon = 'ban';
            color = 'gray';
        }
        return (
            <div className={classNames('return-count', color, active && 'active')}
                 onClick={onClickStatus(type)}>
                <Loader active={loadingCounts}/>
                <AwesomeIcon name={icon} className='count-icon'/>
                <label className='count-label'><span className='count-num'>{count}</span> {label}</label>
            </div>
        )
    }

    return (
        <>
            <InfoBanner featureKey='returns' variant='info'
                        title='Retouren'
                        message='Bekijk retour statussen van bol. Maak creditfacturen en bekijk redenen.'
                        showForNewUsersOnly={true}/>
            <div className='returns'>
                {renderCounts()}
                <div className='returns-row'>
                    <div className='filter-bar'>
                        <BolmateInput onChange={onChangeSearch} value={search} onEnter={onEnterSearch}
                                      label='Retour zoeken'
                                      inputProps={{placeholder: 'Zoeken...'}} className='search-field' ornaments={{
                            element: <AwesomeIcon name='search' className='search-icon'/>,
                            placement: ElementPosition.LEFT
                        }} unControlled/>
                        <BolmateSelect value={status} onChange={onChangeStatus} items={statusItems} label='Retourstatus'
                                       className='select-field' dynamicWidth/>
                    </div>
                    {renderReturnsContainer()}
                    <div className='returns-footer'>
                        {renderPagerCount()}
                        {renderPager('prev')}
                        {renderPager('next')}
                    </div>
                </div>
            </div>
        </>
    )
}
